import { CurrencyCode, UnknownEvent, useSession } from '@faststore/sdk'
import { sendAnalyticsEvent } from '@faststore/sdk/dist/analytics/sendAnalyticsEvent'
import { useEffect, useRef, useState } from 'react'
import { CarouselAnalyticsItem } from 'src/components/sections/Clerkio/typings'
import { buildItemToAddToCart } from 'src/components/sections/Clerkio/utils'
import { addCartItem } from '../cart/handleCartItems'
import { useCart } from '../cart/useCart'
import { useApplicationInsightsAPI } from '../insights/useApplicationInsightsAPI'
import { usePageTypeContext } from 'src/contexts/page-type-context'

export const useCarousel = () => {
  const { id, addItem: defaultAddItem } = useCart()
  const {
    currency: { code },
    person,
  } = useSession()

  const [isVisible, setIsVisible] = useState(false)
  const [addedProductName, setAddedProductName] = useState<string>('')
  const { trackCustomEvent } = useApplicationInsightsAPI()
  const { pageType } = usePageTypeContext()

  const sliderData = useRef({
    isDown: false,
    startX: 0,
    scrollLeft: 0,
    bodyW: 0 as number,
    sliderInnerContainerW: 0,
    sliderModeBreakpoint: 0,
    slider: `<div></div>` as unknown as HTMLElement,
  })

  const DEFAULT_OPTIONS = {
    attributes: true,
    childList: true,
    characterData: true,
    subtree: true,
  }

  useEffect(() => {
    sliderData.current = {
      ...sliderData.current,
      slider: document.createElement('div') as HTMLElement,
    }
  }, [])

  const updateArrow = () => {
    const prevButton = sliderData.current.slider.querySelectorAll<HTMLElement>(
      '.slider-carousel-button.prev'
    )

    const nextButton = sliderData.current.slider.querySelectorAll<HTMLElement>(
      '.slider-carousel-button.next'
    )

    if (prevButton?.length && nextButton?.length) {
      if (sliderData.current.slider.scrollLeft <= 0) {
        prevButton[0].style.opacity = '0.1'
        sliderData.current.slider.classList.add('only-right')
      } else {
        prevButton[0].style.opacity = '1'
        sliderData.current.slider.classList.remove('only-right')

        if (
          sliderData.current.bodyW + sliderData.current.slider.scrollLeft >=
          sliderData.current.sliderInnerContainerW
        ) {
          nextButton[0].style.opacity = '0.1'
          sliderData.current.slider.classList.add('only-left')
        } else {
          nextButton[0].style.opacity = '1'
          sliderData.current.slider.classList.remove('only-left')
        }
      }
    }
  }

  const resizeWindow = () => {
    sliderData.current.slider.scrollLeft = 0
    sliderData.current.bodyW = sliderData.current.slider.clientWidth + 10
    updateArrow()
  }

  const runSlider = () => {
    const sliderInnerContainer =
      sliderData.current.slider.querySelectorAll<HTMLElement>(`:scope > div`)

    sliderData.current.sliderInnerContainerW =
      sliderInnerContainer[0].getBoundingClientRect().width

    sliderData.current.bodyW = sliderData.current.slider.clientWidth + 10

    sliderData.current.sliderModeBreakpoint = sliderData.current.slider.dataset
      .sliderbreakpoint
      ? Number(sliderData.current.slider.dataset.sliderbreakpoint)
      : 0

    sliderData.current.slider.addEventListener('scroll', function () {
      updateArrow()
    })

    const sliderAnimation = (
      slider: HTMLElement,
      carouselButtons: NodeListOf<HTMLElement>,
      move: number
    ) => {
      slider.scrollTo({
        left: move,
        behavior: 'smooth',
      })

      carouselButtons.forEach((el) => {
        el.classList.remove('disabled')
        el.style.opacity = '1'
      })
    }

    sliderData.current.slider.addEventListener('click', (event: MouseEvent) => {
      const clickedElement = event.target as HTMLElement

      if (
        clickedElement?.classList.contains('slider-carousel-button') &&
        !clickedElement.classList.contains('disabled')
      ) {
        if (
          sliderData.current.sliderModeBreakpoint > 0 &&
          window.innerWidth < sliderData.current.sliderModeBreakpoint
        ) {
          return
        }

        const widthCardDesktop = Number(
          sliderData.current.slider.dataset.widthcard
        )

        const entireCardVisible = Math.floor(
          (sliderData.current.bodyW - 40) / widthCardDesktop
        )

        const walk = widthCardDesktop * entireCardVisible

        if (clickedElement?.classList.contains('next')) {
          if (
            sliderData.current.bodyW + sliderData.current.slider.scrollLeft >=
            sliderData.current.sliderInnerContainerW
          ) {
            return
          }
        } else if (sliderData.current.slider.scrollLeft <= 0) {
          return
        }

        const move = clickedElement?.classList.contains('next')
          ? sliderData.current.slider.scrollLeft + walk
          : sliderData.current.slider.scrollLeft - walk

        const carouselButtons =
          sliderData.current.slider.querySelectorAll<HTMLElement>(
            '.slider-carousel-button'
          )

        carouselButtons.forEach((el: any) => {
          el.classList.add('disabled')
          el.style.opacity = '0.1'
        })

        sliderAnimation(sliderData.current.slider, carouselButtons, move)
        updateArrow()
      }
    })

    window.addEventListener('resize', resizeWindow)

    updateArrow()
  }

  const pushAddToCartAnalyticsEvent = (
    item: CarouselAnalyticsItem,
    index: number
  ) => {
    const isKit = item?.item?.isKit
    sendAnalyticsEvent<UnknownEvent>({
      name: 'add_to_cart',
      params: {
        currency: code as CurrencyCode,
        value: item?.item?.price * item?.item?.quantity,
        orderFormId: id,
        // Adding optional kit parameters
        ...(isKit && {
          bundle_label: item?.item?.itemOffered?.name,
          bundle_discount: +(item?.item?.listPrice - item?.item?.price).toFixed(
            2
          ),
          bundle_discount_percentage: +(
            1 -
            item?.item?.price / item?.item?.listPrice
          ).toFixed(2),
        }),
        items: [
          {
            index: index,
            item_list_id: item?.carouselName,
            item_category: item?.category?.split(','),
            item_list_name: item?.carouselName,
            item_id: item.item?.id,
            item_name: item.item?.itemOffered?.name,
            item_brand: item.item?.itemOffered?.brand?.name,
            item_variant: item.item?.itemOffered?.sku,
            quantity: item.item?.quantity,
            price: item.item?.price,
            discount: +(item.item?.listPrice - item.item?.price).toFixed(2),
            full_price: item.item?.listPrice,
            discount_percentage: +(
              1 -
              item.item?.price / item.item?.listPrice
            ).toFixed(2),
            currency: code as CurrencyCode,
            item_variant_name: null,
            product_reference_id: null,
            coupon: null,
            box_type: 'carousel',
            box_quantity: 1,
            box_discount: 0,
            // Adding optional kit parameters
            ...(isKit && {
              bundle_label: item?.item?.itemOffered?.name,
              bundle_discount: +(
                item?.item?.listPrice - item?.item?.price
              ).toFixed(2),
              bundle_discount_percentage: +(
                1 -
                item?.item?.price / item?.item?.listPrice
              ).toFixed(2),
            }),
          },
        ],
      },
    })
  }

  const pushViewItemListAnalyticsEvent = (
    elements: NodeListOf<HTMLElement>
  ) => {
    sendAnalyticsEvent<UnknownEvent>({
      name: 'view_item_list',
      params: {
        items: Array.from(elements).map((el: HTMLElement, index: number) => {
          const item = buildItemToAddToCart(el)
          const isKit = item?.item?.isKit

          return {
            index: index,
            item_list_id: item?.carouselName,
            item_category: item?.category?.split(','),
            item_list_name: item?.carouselName,
            item_id: item.item?.id,
            item_name: item.item?.itemOffered?.name,
            item_brand: item.item?.itemOffered?.brand?.name,
            item_variant: item.item?.itemOffered?.sku,
            quantity: item.item?.quantity,
            price: item.item?.price,
            discount: +(item.item?.listPrice - item.item?.price).toFixed(2),
            full_price: item.item?.listPrice,
            discount_percentage: +(
              1 -
              item.item?.price / item.item?.listPrice
            ).toFixed(2),
            currency: code as CurrencyCode,
            item_variant_name: null,
            product_reference_id: null,
            coupon: null,
            // Adding optional kit parameters
            ...(isKit && {
              bundle_label: item?.item?.itemOffered?.name,
              bundle_discount: +(
                item?.item?.listPrice - item?.item?.price
              ).toFixed(2),
              bundle_discount_percentage: +(
                1 -
                item?.item?.price / item?.item?.listPrice
              ).toFixed(2),
            }),
          }
        }),
      },
    })
  }

  const pushSelectItemListAnalyticsEvent = (
    _: MouseEvent | TouchEvent,
    item: CarouselAnalyticsItem,
    index: number
  ) => {
    const isKit = item?.item?.isKit
    sendAnalyticsEvent<UnknownEvent>({
      name: 'select_item',
      params: {
        // Adding optional kit parameters
        ...(isKit && {
          bundle_label: item?.item?.itemOffered?.name,
          bundle_discount: +(item?.item?.listPrice - item?.item?.price).toFixed(
            2
          ),
          bundle_discount_percentage: +(
            1 -
            item?.item?.price / item?.item?.listPrice
          ).toFixed(2),
        }),
        items: [
          {
            index: index,
            item_list_id: item?.carouselName,
            item_category: item?.category?.split(','),
            item_list_name: item?.carouselName,
            item_id: item.item?.id,
            item_name: item.item?.itemOffered?.name,
            item_brand: item.item?.itemOffered?.brand?.name,
            item_variant: item.item?.itemOffered?.sku,
            quantity: item.item?.quantity,
            price: item.item?.price,
            discount: +(item.item?.listPrice - item.item?.price).toFixed(2),
            full_price: item.item?.listPrice,
            discount_percentage: +(
              1 -
              item.item?.price / item.item?.listPrice
            ).toFixed(2),
            currency: code as CurrencyCode,
            item_variant_name: null,
            product_reference_id: null,
            coupon: null,
            box_type: 'carousel',
            box_quantity: 1,
            box_discount: 0,
            // Adding optional kit parameters
            ...(isKit && {
              bundle_label: item?.item?.itemOffered?.name,
              bundle_discount: +(
                item?.item?.listPrice - item?.item?.price
              ).toFixed(2),
              bundle_discount_percentage: +(
                1 -
                item?.item?.price / item?.item?.listPrice
              ).toFixed(2),
            }),
          },
        ],
      },
    })
  }

  const handleClickEvent = (
    e: MouseEvent | TouchEvent,
    item: CarouselAnalyticsItem,
    index: number
  ) => {
    const currentTarget = e.currentTarget as HTMLElement
    const elInnerHTML = currentTarget.innerHTML
    const loader = document.createElement('div')
    loader.classList.add('clerkio-loader')
    currentTarget.innerHTML = String(loader.outerHTML)

    addCartItem(item?.item, id)
      .then(() => {
        defaultAddItem(item?.item)
      })
      .then(() => {
        setIsVisible(true)
        setAddedProductName(item?.item?.itemOffered.name ?? '')

        pushAddToCartAnalyticsEvent(item, index)

        try {
          trackCustomEvent(
            {
              userId: person?.email,
              cartId: id,
              productId: item?.item?.id,
              sku: item?.item?.itemOffered?.sku,
              url: location.href,
              pageType: pageType,
              actionTrigger: 'add-to-cart-button-carousel',
            },
            'add-to-cart'
          )
        } catch (er) {
          console.log(er)
        }

        setTimeout(() => {
          setIsVisible && setIsVisible(false)
          currentTarget.innerHTML = elInnerHTML
        }, 2000)
      })
  }

  const bindCarouselEvents = (slider: HTMLElement | null | undefined) => {
    const cards = slider?.querySelectorAll<HTMLElement>(
      '[id^="clerkio-item-wrapper-"]'
    )

    cards && pushViewItemListAnalyticsEvent(cards)

    cards?.forEach((el: HTMLElement, index: number) => {
      const buyButton = el?.getElementsByClassName(
        'clerkio-buy-button'
      )[0] as HTMLElement

      const anchor = el?.getElementsByTagName('a')[0] as HTMLElement

      const item = buildItemToAddToCart(el)

      buyButton?.addEventListener('click', (e) =>
        handleClickEvent(e, item, index)
      )

      anchor?.addEventListener('click', (e) =>
        pushSelectItemListAnalyticsEvent(e, item, index)
      )
    })
  }

  const initialize = (
    observer: MutationObserver,
    carouselWrapper: HTMLDivElement | null
  ) => {
    const slider =
      carouselWrapper?.querySelector<HTMLElement>('.carousel-slider')

    if (slider != null) {
      sliderData.current.slider = slider
      runSlider()
      bindCarouselEvents(slider)
      observer.disconnect()
    }
  }

  const carouselInitialization = (
    carouselWrapper: React.RefObject<HTMLDivElement | null>
  ) => {
    const observer = new MutationObserver(
      (_: MutationRecord[], observer: MutationObserver) => {
        initialize(observer, carouselWrapper?.current)
      }
    )

    const slider =
      carouselWrapper?.current?.querySelector<HTMLElement>('.carousel-slider')

    if (slider) {
      initialize(observer, carouselWrapper?.current)
    } else {
      observer.observe(carouselWrapper?.current as Node, DEFAULT_OPTIONS)
    }
  }

  return {
    sliderData,
    runSlider,
    resizeWindow,
    carouselInitialization,
    isVisible,
    setIsVisible,
    addedProductName,
    setAddedProductName,
  }
}
